/**
 * Class representing the main menu navigation.
 */
class Menu {
  private DESKTOP_BREAKPOINT = 1024;
  private siteNavigation: HTMLElement | null;
  private menuContainer: HTMLElement | null;
  private navWrapper: HTMLElement | null;
  private toggleHamburgerButton: HTMLElement | null;
  private menuToggleWrapper: HTMLElement | null;
  private navLogo: HTMLElement | null;
  private spacerDiv: HTMLElement | null;
  private menus: NodeListOf<HTMLDetailsElement> | null;
  private navOverlay: HTMLElement;
  private mobileView: MediaQueryList;
  private toggleButtonActive = false;
  private overlayActive = false;
  private lastScrollTop = 0;
  private scrollThreshold = 10;

  constructor() {
    this.siteNavigation = document.getElementById("site-navigation");
    if (!this.siteNavigation) return;

    this.menuContainer = this.siteNavigation.querySelector(".menu-container");
    if (!this.menuContainer) return;

    this.navWrapper = this.siteNavigation.querySelector(".top-menu__nav-wrapper");
    this.toggleHamburgerButton = this.siteNavigation.querySelector(".menu-toggle");
    this.menuToggleWrapper = this.siteNavigation.querySelector(".menu-toggle__wrapper");
    this.navLogo = this.siteNavigation.querySelector(".o-navbar__logo");
    this.spacerDiv = document.querySelector(".spacer");
    this.menus = this.menuContainer.querySelectorAll(".top-menu__details");
    
    this.navOverlay = document.createElement("div");
    this.navOverlay.classList.add("nav-overlay");
    
    const breakpoint = `(max-width: ${this.DESKTOP_BREAKPOINT - 1}px)`;
    this.mobileView = window.matchMedia(breakpoint);

    this.init();
  }

  #displayDropdownMenu(menuItem: HTMLDetailsElement, displayStyle: string) {
    const dropdownMenu = menuItem.querySelector(".dropdown-menu") as HTMLElement;
    if (dropdownMenu) {
      dropdownMenu.style.display = displayStyle;
    }
  }

  handleNonMenuClick = (e: PointerEvent) => {
    const target = e.target as HTMLElement;

    // Close open menus if the user clicks on a non-menu item
    if (
      this.siteNavigation &&
      this.siteNavigation !== target &&
      !this.siteNavigation.contains(target)
    ) {
      this.menus?.forEach((thisDetail) => {
        if (thisDetail.open) {
          thisDetail.removeAttribute("open");
          this.navOverlay.remove();
          document.documentElement.classList.remove("no-scroll");
        }
      });
    }

    // Close overlay when clicking on menu label or button links
    if (
      this.navOverlay &&
      (target.classList.contains("top-menu__label") || 
       target.classList.contains("top-menu__anchor")) &&
      !this.mobileView.matches
    ) {
      this.navOverlay.remove();
      document.documentElement.classList.remove("no-scroll");
    }
  };

  handleEscapeKey = (e: KeyboardEvent) => {
    // Close open menus if the esc key is pressed
    if (e.key === "Escape") {
      this.menus?.forEach((thisDetail) => {
        if (thisDetail.open) {
          thisDetail.removeAttribute("open");
        }
      });
      this.navOverlay.remove();
      document.documentElement.classList.remove("no-scroll");
      
      // Also close mobile menu if open
      if (this.toggleButtonActive) {
        this.deactivateButton();
        this.deactivateOverlay();
      }
    }
  };

  asyncDropdown = () => {
    if (this.menuContainer) {
      // Close an open menu if another menu item is opened
      this.menus?.forEach((thisDetail, _, details) => {
        thisDetail.ontoggle = () => {
          if (thisDetail.open) {
            
            // Lower hamburger and logo z-index so dropdown header is above them
            if (this.menuToggleWrapper) {
              this.menuToggleWrapper.style.zIndex = "1";
            }
            if (this.navLogo) {
              this.navLogo.style.zIndex = "1";
            }

            // Close other menus
            details.forEach((thatDetail) => {
              if (thatDetail !== thisDetail) {
                thatDetail.removeAttribute("open");
              }
            });

            // Always remove overlay first (if it exists)
            this.navOverlay.remove();

            // Generate overlay only on desktop
            if (!this.mobileView.matches && this.siteNavigation) {
              this.siteNavigation.insertAdjacentElement("afterend", this.navOverlay);
              document.documentElement.classList.add("no-scroll");
            }
          } else {
            // When closing, only remove overlay if no other dropdowns are open
            const anyOpen = Array.from(details).some(detail => detail.open);
            if (!anyOpen) {
              // Restore hamburger and logo z-index
              if (this.menuToggleWrapper) {
                this.menuToggleWrapper.style.zIndex = "";
              }
              if (this.navLogo) {
                this.navLogo.style.zIndex = "";
              }
              this.navOverlay.remove();
              document.documentElement.classList.remove("no-scroll");
            }
          }
        };
      });

      document.addEventListener("pointerdown", this.handleNonMenuClick);
      document.addEventListener("keydown", this.handleEscapeKey);
      window.addEventListener("pageshow", this.backClick);
    }
  };

  backClick = () => {
    this.menus?.forEach((thisDetail) => {
      if (thisDetail.open) {
        thisDetail.removeAttribute("open");
        this.navOverlay.remove();
        document.documentElement.classList.remove("no-scroll");
      }
    });
  };

  activateButton = () => {
    this.showMenu();
    this.toggleHamburgerButton?.setAttribute("aria-expanded", "true");
    this.toggleButtonActive = true;
    this.menuToggleWrapper?.classList.add("open");
  };

  deactivateButton = () => {
    this.resetMenu();
    this.toggleHamburgerButton?.setAttribute("aria-expanded", "false");
    this.menuToggleWrapper?.classList.remove("open");
    this.toggleButtonActive = false;
  };

  resetMenu = () => {
    this.menuContainer?.classList.remove("show");
    this.spacerDiv?.classList.remove("show");
  };

  showMenu = () => {
    this.menuContainer?.classList.add("show");
    this.spacerDiv?.classList.add("show");
  };

  activateOverlay = () => {
    document.documentElement.classList.add("no-scroll");
    this.siteNavigation?.insertAdjacentElement("afterend", this.navOverlay);
    this.overlayActive = true;
  };

  deactivateOverlay = () => {
    this.resetMenu();
    document.documentElement.classList.remove("no-scroll");
    this.navOverlay.remove();
    this.overlayActive = false;
  };

  handleToggleHamburgerButton = () => {
    this.toggleHamburgerButton?.addEventListener("click", () => {
      if (!this.toggleButtonActive) {
        this.activateButton();
        this.activateOverlay();
      } else {
        this.deactivateButton();
        this.deactivateOverlay();
      }
    });
  };

  initBackArrowButtons = () => {
    const backArrows = this.menuContainer?.querySelectorAll(".dropdown-menu__back-arrow");
    backArrows?.forEach((btn) => {
      btn.addEventListener("click", () => {
        const details = btn.closest(".top-menu__details") as HTMLDetailsElement;
        if (!details) return;

        // Check if a non-first column is open
        const columns = details.querySelectorAll('.dropdown-menu__column');
        const nonFirstOpen = Array.from(columns).slice(1).some(col => col.classList.contains('is-open'));

        if (nonFirstOpen) {
          // Reset to default: first column open, others closed
          columns.forEach((col, i) => {
            if (i === 0) {
              col.classList.add('is-open');
            } else {
              col.classList.remove('is-open');
            }
          });
        } else {
          // Already at default state, go back: close the entire dropdown
          details.removeAttribute("open");
        }
      });
    });

    const closeButtons = this.menuContainer?.querySelectorAll(".dropdown-menu__close");
    closeButtons?.forEach((btn) => {
      btn.addEventListener("click", () => {
        // Close all dropdowns
        this.menus?.forEach((detail) => {
          detail.removeAttribute("open");
        });

        // Close mobile menu and overlay
        if (this.toggleButtonActive) {
          this.deactivateButton();
          this.deactivateOverlay();
        }
      });
    });
  };

  init = () => {
    this.handleToggleHamburgerButton();
    this.asyncDropdown();
    this.initDropdownAccordions();
    this.initBackArrowButtons();
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  };

  /**
   * Hide the navbar when scrolling down and show it when scrolling up on mobile, but only if the menu is not open
   */
  handleScroll = () => {
    const MenuOpen = this.toggleButtonActive || Array.from(this.menus || []).some(m => m.open);
    const container = this.siteNavigation.querySelector(".o-navbar__container");
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (!this.mobileView.matches || MenuOpen || !this.siteNavigation) return;

    if (!container) return;

    if (Math.abs(this.lastScrollTop - scrollTop) <= this.scrollThreshold) return;

    if (scrollTop > this.lastScrollTop && scrollTop > 100) {
      container.classList.add("is-hidden");
    } else {
      container.classList.remove("is-hidden");
    }

    this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; 
  };

  /**
   * Initialize accordion for dropdown menu columns on mobile/tablet.
   * First column is open by default, others are collapsed with arrows.
   */
  initDropdownAccordions = () => {
    const dropdownColumns = document.querySelectorAll('.dropdown-menu__column');

    dropdownColumns.forEach((column, index) => {
      const header = column.querySelector('.dropdown-menu__column-header') as HTMLElement;
      if (!header) return;

      // Wrap content if not already wrapped
      let contentWrapper = column.querySelector('.dropdown-menu__column-content') as HTMLElement;
      if (!contentWrapper) {
        contentWrapper = document.createElement('div');
        contentWrapper.classList.add('dropdown-menu__column-content');

        const children = Array.from(column.children);
        let foundHeader = false;
        children.forEach(child => {
          if (child === header) {
            foundHeader = true;
          } else if (foundHeader) {
            contentWrapper.appendChild(child);
          }
        });

        column.appendChild(contentWrapper);
      }

      // For non-first columns, add the header title inside the content overlay
      if (index > 0 && !contentWrapper.querySelector('.dropdown-menu__column-content-title')) {
        const title = document.createElement('div');
        title.classList.add('dropdown-menu__column-content-title');
        title.textContent = header.textContent?.trim() || '';
        contentWrapper.insertBefore(title, contentWrapper.firstChild);
      }

      // Mobile/tablet only: set initial state and click handlers
      if (this.mobileView.matches) {
        // Open first column by default
        if (index === 0) {
          column.classList.add('is-open');
        }

        // Toggle on click
        header.addEventListener('click', () => {
          column.classList.toggle('is-open');

          // Animate gem mark when drilling deeper
          if (column.classList.contains('is-open')) {
            const gemLogo = column.closest('.dropdown-menu__wrapper')?.querySelector('.dropdown-menu__symbol-logo');
            if (gemLogo) {
              gemLogo.classList.remove('is-animating');
              void (gemLogo as HTMLElement).offsetWidth; // force reflow
              gemLogo.classList.add('is-animating');
            }
          }
        });
      }
    });

    this.mobileView.addEventListener('change', (e) => {
      if (e.matches) {
        this.initDropdownAccordions();
      }
    });
  };
}

export default () => new Menu();
